<template>
  <div class="min-h-[68vh] w-full">
    <div class="mx-auto mt-14 w-full max-w-xl px-4">
      <!-- VALID TOKEN: show form -->
      <div v-if="status === true"
           class="rounded-2xl bg-white p-6 shadow-[0_8px_24px_rgba(16,24,40,.08)] ring-1 ring-slate-100 md:p-8">

        <h1 class="mb-2 text-2xl font-semibold text-slate-900">Reset password</h1>
        <p class="mb-6 text-[14px] text-slate-500">
          Choose a new password for your account.
        </p>

        <!-- New password -->
        <label class="mb-1 block text-[12px] font-medium text-slate-600 tracking-wide">
          NEW PASSWORD <span class="text-rose-500">*</span>
        </label>
        <div class="relative">
          <input
            :type="showNew ? 'text' : 'password'"
            v-model.trim="password"
            autocomplete="new-password"
            class="block w-full rounded-lg border border-slate-200 px-4 py-3 text-[15px] font-semibold text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
            placeholder="Enter a strong password"
          />
          <button type="button"
                  class="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-50"
                  @click="showNew = !showNew" aria-label="Toggle password visibility">
            <svg v-if="!showNew" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                 viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                 d="M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.214.07.45 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z"/>
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                 d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd"
                d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-2.29-2.29c1.01-.86 1.86-1.95 2.46-3.16.15-.31.15-.67 0-.98C20.58 8.51 16.64 6 12 6c-1.22 0-2.4.19-3.5.54L3.53 2.47Zm10.94 12.06-5.01-5.01A3 3 0 0 0 12 15a3 3 0 0 0 2.47-1.47ZM5.32 7.15 6.8 8.63A12.3 12.3 0 0 0 3.54 12c1.39 3.17 5.33 5.68 8.46 5.68 1.14 0 2.26-.2 3.3-.56l1.49 1.49A12.83 12.83 0 0 1 12 19C7.36 19 3.42 16.49 2.04 12.32a1.6 1.6 0 0 1 0-1.28c.72-2.03 2.14-3.74 3.28-4.89Z"
                clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <!-- Strength bar -->
        <div class="mt-2 flex items-center gap-3">
          <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div class="h-full"
                 :class="strengthBarClass"
                 :style="{ width: strength + '%' }"></div>
          </div>
          <span class="min-w-[52px] text-[12px] font-medium" :class="strengthTextClass">{{ strengthLabel }}</span>
        </div>

        <!-- Repeat password -->
        <div class="mt-6">
          <label class="mb-1 block text-[12px] font-medium text-slate-600 tracking-wide">
            REPEAT PASSWORD <span class="text-rose-500">*</span>
          </label>
          <div class="relative">
            <input
              :type="showRepeat ? 'text' : 'password'"
              v-model.trim="passwordConfirmationn"
              autocomplete="new-password"
              class="block w-full rounded-lg border border-slate-200 px-4 py-3 text-[15px] font-semibold text-slate-800 placeholder-slate-400 outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-50"
              placeholder="Re-enter your password"
            />
            <button type="button"
                    class="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-slate-500 hover:bg-slate-50"
                    @click="showRepeat = !showRepeat" aria-label="Toggle password visibility">
              <svg v-if="!showRepeat" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                   viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                   d="M2.036 12.322a1.012 1.012 0 010-.644C3.423 7.51 7.36 5 12 5c4.64 0 8.577 2.51 9.964 6.678.07.214.07.45 0 .644C20.577 16.49 16.64 19 12 19c-4.64 0-8.577-2.51-9.964-6.678z"/>
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                   d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path fill-rule="evenodd"
                  d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-2.29-2.29c1.01-.86 1.86-1.95 2.46-3.16.15-.31.15-.67 0-.98C20.58 8.51 16.64 6 12 6c-1.22 0-2.4.19-3.5.54L3.53 2.47Zm10.94 12.06-5.01-5.01A3 3 0 0 0 12 15a3 3 0 0 0 2.47-1.47ZM5.32 7.15 6.8 8.63A12.3 12.3 0 0 0 3.54 12c1.39 3.17 5.33 5.68 8.46 5.68 1.14 0 2.26-.2 3.3-.56l1.49 1.49A12.83 12.83 0 0 1 12 19C7.36 19 3.42 16.49 2.04 12.32a1.6 1.6 0 0 1 0-1.28c.72-2.03 2.14-3.74 3.28-4.89Z"
                  clip-rule="evenodd"/>
              </svg>
            </button>
          </div>

          <!-- Inline errors -->
          <p v-if="passwordError" class="mt-2 text-[12px] font-medium text-rose-600">{{ passwordError }}</p>
          <p v-if="confirmError" class="mt-1 text-[12px] font-medium text-rose-600">{{ confirmError }}</p>
        </div>

        <!-- Submit -->
        <div class="mt-7">
          <button
            :disabled="isLoading || !canSubmit"
            @click="handleResetPassword"
            class="inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60">
            <svg v-if="isLoading" class="-ml-1 mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-20"/>
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="4" class="opacity-90" />
            </svg>
            UPDATE PASSWORD
          </button>
        </div>
      </div>

      <!-- INVALID / EXPIRED TOKEN -->
      <div v-else-if="status === 'not-found'"
           class="rounded-2xl border border-rose-100 bg-rose-50 p-6 text-rose-800 shadow-sm md:p-8">
        <div class="flex items-start gap-3">
          <div class="mt-0.5 rounded-full bg-white p-2 text-rose-500 ring-1 ring-rose-200">
            ⚠️
          </div>
          <div>
            <h3 class="text-[16px] font-semibold">Invalid token</h3>
            <p class="mt-1 text-[14px]">The reset link is invalid or has expired.</p>
          </div>
        </div>
      </div>

      <!-- LOADING -->
      <div v-else class="flex items-center justify-center gap-3 py-24 text-slate-600">
        <img class="h-6 w-6 animate-spin" src="/assets/loading.svg" alt="" />
        <span class="text-[14px]">Loading… please wait</span>
      </div>
    </div>
  </div>

  <Notification :show="notif.show" :type="notif.type" :message="notif.message"
                :messageDetails="notif.messageDetails" @closed="notifClosed" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import Auth from '@/composables/Auth'
import { handleApiError } from '@/utils/common'
import type { NotificationType } from '@/types'
import Notification from '@/widgets/Notification.vue'
import router from '@/router'

const props = defineProps<{ token: string }>()

// state
const password = ref<string>('')
const passwordConfirmationn = ref<string>('')

const showNew = ref(false)
const showRepeat = ref(false)

const isLoading = ref(false)
const status = ref<boolean | string>(false) // true | 'not-found' | false
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})

const passwordError = ref<string>('')       // inline error
const confirmError  = ref<string>('')       // inline error

const { resetPassword, checkResetToken }  = Auth()

// strength meter (0..100)
const strength = ref(0)
watch(password, (v) => {
  let score = 0
  if (v.length >= 6) score += 25
  if (/[A-Z]/.test(v)) score += 20
  if (/[a-z]/.test(v)) score += 15
  if (/\d/.test(v)) score += 20
  if (/[^A-Za-z0-9]/.test(v)) score += 20
  strength.value = Math.min(100, score)
})

const strengthLabel = computed(() => {
  if (strength.value < 30) return 'Weak'
  if (strength.value < 70) return 'Medium'
  return 'Strong'
})
const strengthBarClass = computed(() => {
  if (strength.value < 30) return 'bg-orange-400'
  if (strength.value < 70) return 'bg-amber-500'
  return 'bg-emerald-500'
})
const strengthTextClass = computed(() => {
  if (strength.value < 30) return 'text-orange-500'
  if (strength.value < 70) return 'text-amber-600'
  return 'text-emerald-600'
})

const canSubmit = computed(() =>
  password.value.length >= 6 &&
  passwordConfirmationn.value.length >= 6 &&
  password.value === passwordConfirmationn.value
)

const validate = () => {
  passwordError.value = ''
  confirmError.value = ''

  if (!password.value) {
    passwordError.value = 'Password is required'
  } else if (password.value.length < 6) {
    passwordError.value = 'Must be at least 6 characters long'
  }

  if (!passwordConfirmationn.value) {
    confirmError.value = 'Please re-enter the password'
  } else if (password.value && password.value !== passwordConfirmationn.value) {
    confirmError.value = 'Passwords do not match'
  }

  return !(passwordError.value || confirmError.value)
}

const handleResetPassword = async () => {
  if (!validate()) return
  isLoading.value = true
  const response = await resetPassword(password.value, passwordConfirmationn.value, props.token)
  isLoading.value = false

  if (response.status === 200) {
    password.value = ''
    passwordConfirmationn.value = ''
    router.push({ name: 'Login' })
  } else {
    const error = handleApiError(response)
    notif.value = { show: true, type: 'error', message: error, messageDetails: '' }
    setTimeout(() => notifClosed(), 4000)
  }
}

const notifClosed = () => { notif.value.show = false }

onMounted(async () => {
  if (!props.token) {
    status.value = 'not-found'
    return
  }
  const res = await checkResetToken(props.token)
  status.value = res.status === 200 ? true : 'not-found'
})
</script>